// Sugalarm — touch-friendly numeric entry. Owns its own value-string
// state so the LogScreen tabs don't each reimplement digit-handling.
// Decimal point support is gated by `allowDecimal`.

import { useCallback } from "react";

import styles from "./Numpad.module.css";

interface Props {
  value: string;
  onChange: (next: string) => void;
  allowDecimal?: boolean;
  maxLength?: number;
  unit?: string;
  placeholder?: string;
  // Cycle 30: skip rendering the component's own value-display row.
  // Used by the mobile Log screen, which renders its own slimmer
  // readout above the keypad. Defaults to false so kiosk / web stay
  // pixel-identical.
  hideDisplay?: boolean;
}

const DIGIT_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

export function Numpad({
  value,
  onChange,
  allowDecimal = true,
  maxLength = 6,
  unit,
  placeholder = "—",
  hideDisplay = false,
}: Props) {
  const press = useCallback(
    (k: string) => {
      if (k === "BACK") {
        onChange(value.slice(0, -1));
        return;
      }
      if (k === ".") {
        if (!allowDecimal || value.includes(".")) return;
        if (value.length >= maxLength) return;
        onChange(value === "" ? "0." : `${value}.`);
        return;
      }
      // digit
      if (value.length >= maxLength) return;
      // Don't allow a leading zero unless followed by '.'.
      if (value === "0" && k !== ".") {
        onChange(k);
        return;
      }
      onChange(`${value}${k}`);
    },
    [value, onChange, allowDecimal, maxLength],
  );

  return (
    <div>
      {hideDisplay ? null : (
        <div className={styles.display}>
          {value === "" ? (
            <span className={`${styles.value} ${styles.placeholder}`}>
              {placeholder}
            </span>
          ) : (
            <span className={styles.value}>{value}</span>
          )}
          {unit ? <span className={styles.unit}>{unit}</span> : null}
        </div>
      )}
      <div className={styles.grid}>
        {DIGIT_KEYS.map((d) => (
          <button
            key={d}
            type="button"
            className={styles.key}
            onClick={() => press(d)}
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          className={`${styles.key} ${styles.muted}`}
          disabled={!allowDecimal}
          onClick={() => press(".")}
        >
          .
        </button>
        <button
          type="button"
          className={styles.key}
          onClick={() => press("0")}
        >
          0
        </button>
        <button
          type="button"
          className={`${styles.key} ${styles.muted}`}
          onClick={() => press("BACK")}
          aria-label="Backspace"
        >
          ⌫
        </button>
      </div>
    </div>
  );
}
